import { html, nothing, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { customElement, state } from 'lit/decorators.js';
import style from './sector-diversification.scss?inline';
import { PopBaseComponent } from '../../base/pop-base-component.js';
import { Constants, VIEWS } from './sector-diversification.constants.js';
import type { SectorDiversificationContentObject } from './sector-diversification.model.js';
import { dateToMMDDYYYY } from '../../common/utils/formatDate';
import { sanitizeHTML } from '../../common/utils/sanitizeHTML';
import { generateLocalDisclaimerText } from '../../common/utils/generateDisclaimer';

/**
 * @tag pop-sector-diversification-component
 * @property {SectorDiversification} [componentObject] Sector diversification content object
 * @attribute { SectorDiversificationContentObject } [data-component-object] SectorDiversification content object
 */
@customElement(Constants.TAG_NAME)
export class PopSectorDiversificationComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * SectorDiversification content object
   */
  componentObject?: SectorDiversificationContentObject;

  /**
   * @internal
   */
  @state()
  view: string = Constants.DEFAULT_VIEW;

  @state()
  selectedAsOfDate: string | undefined = '';

  initialize(): void {
    super.initialize();

    this.initializeAsOfDate();
  }

  private initializeAsOfDate(): void {
    this.selectedAsOfDate =
      this.componentObject?.sectorDiversificationDetails.asOfDate;
  }

  private renderTopSection() {
    return html`
      <h3
        class="sector-diversification-heading b-typography-serif--headline--100 b-font-weight-bold"
      >
        ${Constants.COMPONENT_TITLE}
      </h3>
    `;
  }

  private renderControlSection() {
    const controlTypeClass =
      this.view === VIEWS.TABLE ? 'table-controls' : 'chart-controls';

    return html`
      <div
        class="sector-diversification-controls ${controlTypeClass} b-margin-block-start--micro--jumbo"
      >
        ${this.view === VIEWS.TABLE ? this.renderAsOfDate() : nothing}
        ${this.renderViewDropdown()}
      </div>
    `;
  }

  private renderAsOfDate() {
    const availableDates =
      this.componentObject?.sectorDiversificationDetails.availablePriorDates ||
      [];

    return html`
      <beacon-control>
        <label slot="label" for="${Constants.DATA_AS_OF_DROPDOWN_ID}">
          ${Constants.DATA_AS_OF_DROPDOWN_LABEL}
        </label>
        <select
          id="${Constants.DATA_AS_OF_DROPDOWN_ID}"
          @change="To be implemented in a future MR"
        >
          ${availableDates.map(
            (date) =>
              html`<option
                value="${date}"
                ?selected=${date === this.selectedAsOfDate}
              >
                ${dateToMMDDYYYY(date)}
              </option>`,
          )}
        </select>
      </beacon-control>
    `;
  }

  private renderViewDropdown() {
    return html` <beacon-control>
      <label slot="label" for="${Constants.VIEW_DROPDOWN_ID}"
        >${Constants.VIEW_DROPDOWN_LABEL}</label
      >
      <select
        id="${Constants.VIEW_DROPDOWN_ID}"
        @change="${this.viewChangeHandler}"
      >
        ${Object.values(VIEWS).map((view) => {
          return html`
            <option value="${view}" ?selected=${view === this.view}>
              ${view}
            </option>
          `;
        })}
      </select>
    </beacon-control>`;
  }

  private viewChangeHandler(event: Event & { target?: { value?: string } }) {
    const value = event.target?.value;
    if (value) {
      this.view = value;
    } else {
      console.warn(`Invalid view value: ${value}`);
    }
  }

  private renderContentSection() {
    return html`
      ${this.view === VIEWS.TABLE
        ? this.renderTableView()
        : this.renderChartView()}
    `;
  }

  private renderTableView() {
    const itemList =
      this.componentObject?.sectorDiversificationDetails
        .sectorDiversification || [];

    if (!itemList.length) {
      return html`<pop-no-data-available-component
        .message="${Constants.NO_TABLE_DATA_AVAILABLE}"
      ></pop-no-data-available-component>`;
    }

    return html`
      <div class="b-margin-block-start--micro--jumbo">
        <pop-sector-diversification-table-component
          data-component-object="${JSON.stringify({
            itemList: itemList,
          })}"
        ></pop-contributors-detractors-table-component>
      </div>
      ${this.renderBenchmarkName()}
    `;
  }

  renderBenchmarkName() {
    return html`<p class="sector-diversification-benchmark">
      <span
        class="b-typography-sans-serif--caption-loose--300 b-font-weight-bold"
        >${Constants.BENCHMARK_PREFIX}</span
      >
      <span
        class="b-typography-sans-serif--caption-loose--300 b-font-weight-light"
        >${this.componentObject?.sectorDiversificationDetails
          .benchmarkName}</span
      >
    </p>`;
  }

  private renderChartView() {
    const itemList =
      this.componentObject?.historicalSectorDiversification
        .sectorDiversificationLists || [];

    if (!itemList.length) {
      return html`<pop-no-data-available-component
        .message="${Constants.NO_CHART_DATA_AVAILABLE}"
      ></pop-no-data-available-component>`;
    }

    return html`
      <pop-sector-diversification-chart-component
        data-component-object="${JSON.stringify({
          itemList: itemList,
        })}"
      ></pop-contributors-detractors-chart-component>
    `;
  }

  private renderDisclaimerSection() {
    const disclaimers =
      this.componentObject?.sectorDiversificationDetails.disclaimers ?? [];

    if (disclaimers.length === 0) {
      return nothing;
    }

    const disclaimerText = generateLocalDisclaimerText(disclaimers);

    return html`<div class="b-margin-block-start--micro--jumbo">
      <pop-disclosure-component data-footnote-indicator-type="symbol">
        ${unsafeHTML(sanitizeHTML(disclaimerText))}
      </pop-disclosure-component>
    </div>`;
  }

  render() {
    return this.renderComponent(html`
      <section class="sector-diversification-content b-container">
        ${this.renderTopSection()} ${this.renderControlSection()}
        ${this.renderContentSection()} ${this.renderDisclaimerSection()}
      </section>
    `);
  }
}
